import { Fonts } from "@/constants/font";
import { Keys } from "@/constants/key";
import { Urls } from "@/constants/urls";
import { Tr } from "@/i18n/i18n";
import ListenerManager from "@/managers/listener-manager";
import { ListEventInfoPanel } from "@/scenes.ui/list-event-info-panel";
import { ListForceRanksPanel } from "@/scenes.ui/list-force-ranks-panel";
import { ListForcesPanel } from "@/scenes.ui/list-forces-panel";
import { ListManualPanel } from "@/scenes.ui/list-manual-panel";
import { ListView } from "@/scenes.ui/list-view";
import store from "@/store";
import { WarriorClass } from "@/store/level/types";
import PlayerStore from "@/store/player";
import { ForceDescription } from "@/store/player/types";
import { RatingsType } from "@/store/ratings/types";
import TaskStore from "@/store/task";
import { BlackoutUtils } from "@/utils/blackout-utils";
import { ListBlackouter } from "@/utils/list-blackouter";
import SoundUtils from "@/utils/sound-utils";
import SpriteUtils from "@/utils/sprite-utils";
import TaskUtils from "@/utils/task-utils";
import TextUtils from "@/utils/text-utils";
import { injectable } from "inversify";
import CU from "../utils/c-u";
import Scene = Phaser.Scene;
import Container = Phaser.GameObjects.Container;
import BaseSound = Phaser.Sound.BaseSound;
import Image = Phaser.GameObjects.Image;

@injectable()
export class ListService {

    public static readonly DEPTH: number = 3000;

    private readonly LIST_VIEW_PANEL_OFFSET: number = 67;
    private scene: Scene | undefined;
    private canvas: HTMLCanvasElement | undefined;

    private mainContainer: ViewContainer | undefined;

    private shift!: number;

    private awardSound!: BaseSound | undefined;
    private levelUpSound!: BaseSound | undefined;
    private selectSound!: BaseSound | undefined;
    private backCallback!: () => Promise<void>;
    private backButton!: Image;

    private static viewByName: Map<string, ViewContainer> = new Map<string, ViewContainer>();

    public showManual(scene: Scene, canvas: HTMLCanvasElement, manualId: string, closeCallback: () => void) {
        let self = this;
        PlayerStore.getManual(manualId).then(manual => {
            scene.load.image(Keys.profileCreature(manualId), `assets/profile/creatures/${manualId}.png`);
            scene.load.once(Phaser.Loader.Events.COMPLETE, () => {
                let cont = new ListManualPanel(scene, manual, manualId);
                self.showCustom(scene, canvas, Tr('spr'), [cont], closeCallback, this.LIST_VIEW_PANEL_OFFSET);
            });
            scene.load.start()
        });
    }

    private static getMaxRank(bestVictory: number) {
        if (bestVictory < 125) {
            return 3;
        }
        if (bestVictory < 500) {
            return 5;
        }
        if (bestVictory < 1375) {
            return 7;
        }
        if (bestVictory < 1750) {
            return 11;
        }
        if (bestVictory < 6750) {
            return 16;
        }
        if (bestVictory <= 10000) {
            return 20;
        }
        if (bestVictory <= 20000) {
            return 20;
        }
        if (bestVictory <= 30000) {
            return 24;
        }
        return 30;
    }

    public showForcesManual(scene: Scene, canvas: HTMLCanvasElement, closeCallback: () => void) {
        let self = this;
        let bestVictory = PlayerStore.getPlayer().bestVictoryStair;
        let maxRank = ListService.getMaxRank(bestVictory);
        PlayerStore.getForcesShortDescriptions().then((forceShortDescription) => {
            let cont = new ListForcesPanel(scene, forceShortDescription, (wc: WarriorClass) => {
                let forceDescription = PlayerStore.GET_FORCE_DESCRIPTION(wc);
                if(forceDescription == undefined){
                    PlayerStore.getForceDescription(wc).then((forceDescription) => {
                        self.showForceRanksManual(scene, canvas, wc, forceDescription, closeCallback, maxRank);
                    });
                } else {
                    self.showForceRanksManual(scene, canvas,  wc, forceDescription,closeCallback, maxRank);
                }
            });

            self.showCustom(scene, canvas, Tr('spr.forces'), [cont], closeCallback, this.LIST_VIEW_PANEL_OFFSET);
            scene.time.delayedCall(300, () => {
                closeCallback();
            })
        });
    }

    public showEventPrizesInfo(scene: Scene, canvas: HTMLCanvasElement, closeCallback: () => void) {
        let self = this;
        TaskStore.fetchEventRewardsInfo().then(positions => {
            scene.load.once(Phaser.Loader.Events.COMPLETE, () => {
                let cont = new ListEventInfoPanel(scene, positions, () => self.openRatingPage());
                self.showCustom(scene, canvas, TaskUtils.getEventDescHeader(), [cont], closeCallback, this.LIST_VIEW_PANEL_OFFSET);
            });
            scene.load.start()
        });
    }

    private openRatingPage(): void {
        CU.blockCanvas(this.scene!);
        ListenerManager.callRatingCallback();
        store.commit('SET_RATINGS_TYPE', RatingsType.PO_EVENTU);
    }

    public showForceRanksManual(scene: Scene, canvas: HTMLCanvasElement,wc:WarriorClass, forceDescription: ForceDescription,
                                closeCallback: () => void, rankLimit?:number) {
        let self = this;
        let cont = new ListForceRanksPanel(scene, wc, forceDescription, rankLimit);
        self.showCustom(scene, canvas, Tr('force.ranks'), [cont], closeCallback, this.LIST_VIEW_PANEL_OFFSET);

        scene.time.delayedCall(500, () => {
            this.setBackCallback(() => {
                return new Promise<void>((resolve) => {
                    self.showForcesManual(scene, canvas, closeCallback);
                    resolve();
                });
            });
        });
    }

    public show(scene: Scene, canvas: HTMLCanvasElement, titleText: string, children: Container[], closeCallback: () => void) {
        this.showCustom(scene, canvas, titleText, children, closeCallback, this.LIST_VIEW_PANEL_OFFSET);
    }

    public showCustom(scene: Scene, canvas: HTMLCanvasElement, titleText: string, children: Container[], closeCallback: () => void, offset: number) {
        this.scene = scene;
        this.canvas = canvas;

        const self = this;

        BlackoutUtils.blackout(scene, canvas, () => {
            self.awardSound = SoundUtils.addSound(self.scene!, Keys.SOUND_AWARD);
            self.selectSound = SoundUtils.addSound(self.scene!, Keys.SOUND_SELECT);
            self.levelUpSound = SoundUtils.addSound(self.scene!, Keys.SOUND_LEVEL_UP);

            this.init(scene, canvas, titleText, children, closeCallback, offset, false);

            return this;
        });
    }

    public showSmart(scene: Scene, canvas: HTMLCanvasElement, closeCallback: () => void) : ListBlackouter {
        return this.showSmartSpecial(scene, canvas, closeCallback, false);
    }

    public showSmartSpecial(scene: Scene, canvas: HTMLCanvasElement, closeCallback: () => void, noDestroyOnClose: boolean) : ListBlackouter {
        this.scene = scene;
        this.canvas = canvas;

        const self = this;

        let blackouter: ListBlackouter = new ListBlackouter();

        blackouter.blackout(scene, canvas, (children: Container[], titleText: string) => {
            self.awardSound = SoundUtils.addSound(self.scene!, Keys.SOUND_AWARD);
            self.selectSound = SoundUtils.addSound(self.scene!, Keys.SOUND_SELECT);
            self.levelUpSound = SoundUtils.addSound(self.scene!, Keys.SOUND_LEVEL_UP);

            this.init(scene, canvas, titleText, children, closeCallback, self.LIST_VIEW_PANEL_OFFSET, noDestroyOnClose);

            return this;
        });
        return blackouter;
    }

    public showCustomSmart(scene: Scene, canvas: HTMLCanvasElement, closeCallback: () => void, offset: number): ListBlackouter {
        this.scene = scene;
        this.canvas = canvas;

        const self = this;

        let blackouter: ListBlackouter = new ListBlackouter();

        blackouter.blackout(scene, canvas, (children: Container[], titleText: string) => {
            self.awardSound = SoundUtils.addSound(self.scene!, Keys.SOUND_AWARD);
            self.selectSound = SoundUtils.addSound(self.scene!, Keys.SOUND_SELECT);
            self.levelUpSound = SoundUtils.addSound(self.scene!, Keys.SOUND_LEVEL_UP);

            this.init(scene, canvas, titleText, children, closeCallback, offset, false);

            return this;
        });
        return blackouter;
    }

    public init(scene: Scene, canvas: HTMLCanvasElement, titleText: string, children: Container[], closeCallback: () => void,
                offset: number, noDestroyOnClose: boolean = false) {
        let self = this;
        let y = 550;
        self.shift = offset;

        let height = canvas.height - 80 * CU.S;

        if (self.mainContainer) {
            self.mainContainer.destroy();
        }

        self.mainContainer = new ViewContainer(scene, canvas, 0, y, titleText, self.selectSound, closeCallback, offset, noDestroyOnClose);
        self.scene!.add.existing(self.mainContainer!);

        if (children && children.length > 0) {
            let listView: ListView = new ListView(this.scene!, this.canvas!, children, 0, this.shift * CU.S, CU.CANVAS_WIDTH, (height - offset * CU.S));
            self.mainContainer!.add(listView);
        } else {
            let noElements = TextUtils.addTextAdvanced(this.scene!, Tr('spisok.pust'), 300, 400,
                '#ffffff', 24, 'normal', 300, 'center', 1, Fonts.ARIAL_MT);
            self.mainContainer.add(noElements);
        }

        let separator = SpriteUtils.addSprite(this.scene!, 0, this.shift - 8, Keys.ATLAS_SHOP_UI, Keys.UI_SHOP_SEPARATOR_LARGE).setOrigin(0, 0);
        self.mainContainer!.add(separator);
        separator = SpriteUtils.addSprite(this.scene!, 0, height / CU.S, Keys.ATLAS_SHOP_UI, Keys.UI_SHOP_SEPARATOR_LARGE).setOrigin(0, 0);
        self.mainContainer!.add(separator);

        self.mainContainer!.setupXButton(scene, canvas, self.selectSound);
        this.backButton = self.mainContainer!.setupBackButton(scene, canvas, self.selectSound, ()=>{return self.backCallback()});

        ListService.viewByName.set(titleText, self.mainContainer!);
    }

    public setBackCallback(backCallback: () => Promise<void>) {
        this.backCallback = backCallback;
        this.backButton.setVisible(true);
    }

    public close(id: string): void {
        let viewMainCont = ListService.viewByName.get(id);
        if (viewMainCont) {
            viewMainCont.destroyContainer();
        }
    }
}

export class ViewContainer extends Container {

    private closeCallback!: () => void;

    private noDestroyOnClose!: boolean;

    constructor(scene: Scene, canvas: HTMLCanvasElement, x: number, y: number, titleText: string,
                selectSound: BaseSound | undefined, closeCallback: () => void, offset: number, noDestroyOnClose: boolean) {
        super(scene);

        let self = this;
        this.noDestroyOnClose = noDestroyOnClose;

        this.closeCallback = closeCallback;

        this.setDepth(ListService.DEPTH);

        const background = SpriteUtils.addSprite(scene!, CU.ETALON_CANVAS_WIDTH / 2, y, Keys.SHOP_BACK).setOrigin(0.5, 0.5).setInteractive();

        let shopBottom = SpriteUtils.addSprite(scene!, canvas!.width / 2 / CU.S, canvas!.height / CU.S, Keys.SHOP_BOTTOM).setOrigin(0.5, 1);
        SpriteUtils.setDispSize(shopBottom, canvas!.width, canvas!.height);

        const titleLight = SpriteUtils.addSprite(scene!, 300, offset - 37, Keys.ATLAS_SHOP_UI, Keys.UI_SHOP_TITLE_LIGHT).setOrigin(0.5, 0.5).setInteractive();
        const title = TextUtils.addTextByFamily(scene!, titleText, 300, offset - 33, Urls.CURRENT_TEXT_STYLE, 24,
            'normal', 420, Fonts.BENGALY_NORMAL)
            .setOrigin(0.5, 0.5).setStroke('#000000', 1);

        this.add([shopBottom, background, titleLight, title]);
    }

    public setupXButton(scene: Scene, canvas: HTMLCanvasElement, selectSound: BaseSound | undefined) {
        let self = this;
        const xButton = SpriteUtils.addImage(scene!, CU.ETALON_CANVAS_WIDTH * 0.95, CU.ETALON_CANVAS_WIDTH * 0.05, Keys.ATLAS_SHOP_UI, Keys.UI_SHOP_X).setOrigin(0.5, 0.5);

        const clickShield = scene!.add.rectangle(canvas.width, 0, 120 * CU.S, 70 * CU.S).setOrigin(1, 0).setInteractive();

        xButton.setInteractive().on('pointerup', function() {
            SoundUtils.play(selectSound);
            self.destroyContainer();
        });
        this.add([clickShield, xButton]);
    }

    public setupBackButton(scene: Scene, canvas: HTMLCanvasElement, selectSound: BaseSound | undefined, backCallback: () => Promise<void>) {
        let self = this;
        const backButton = SpriteUtils.addImage(scene!, CU.ETALON_CANVAS_WIDTH * 0.05, CU.ETALON_CANVAS_WIDTH * 0.05, Keys.ATLAS_PROFILE, Keys.UI_ARROW_LEFT).setOrigin(0.5, 0.5);
        backButton.visible = false;

        const clickShield = scene!.add.rectangle(0, 0, 120 * CU.S, 70 * CU.S).setOrigin(1, 0).setInteractive();

        backButton.setInteractive().on('pointerup', function() {
            SoundUtils.play(selectSound);
            backCallback().then(() => {
                self.destroyContainer();
            });
        });
        this.add([clickShield, backButton]);
        return backButton;
    }



    public destroyContainer() {
        this.setVisible(false);
        this.destroy();

        if (this.closeCallback) {
            this.closeCallback();
        }
    }

}
